<template>
  <MaxControl v-if="CORE_INPUTS.includes(node.input)" :node="node" />
  <div v-else class="form-group max-pos-field" data-max-host>
    <label v-if="node.input === 'toggle'" class="checkbox">
      <input
        type="checkbox"
        :checked="node.value === true"
        :disabled="disabled"
        v-on:change="commit($event.target.checked)"
      />
      <span :class="{ 'sr-only': node.hideLabel }">{{ node.label }}</span>
    </label>
    <template v-else>
      <label :class="node.hideLabel ? 'sr-only' : 'control-label'" :for="fieldId">{{ node.label }}</label>
      <MaxPosPicker
        v-if="node.input === 'select' || node.input === 'multiselect'"
        :options="node.options"
        :modelValue="node.value"
        :multiple="node.input === 'multiselect'"
        :placeholder="node.placeholder || node.label"
        :disabled="disabled"
        :optionsState="node.optionsState"
        v-on:update:modelValue="commit"
      />
      <textarea
        v-else-if="node.input === 'textarea'"
        :id="fieldId"
        class="form-control"
        rows="3"
        :value="node.value ?? ''"
        :placeholder="node.placeholder"
        :disabled="disabled"
        v-on:input="typed($event.target.value)"
        v-on:change="commit($event.target.value)"
      />
      <DatePicker
        v-else-if="node.input === 'date'"
        :modelValue="node.value || null"
        :shortLocale="shortLocale"
        :staticInput="true"
        :placeholder="node.placeholder || node.label"
        :disabled="disabled"
        v-on:update:modelValue="commit(toIso($event))"
      />
      <input
        v-else-if="node.input === 'datetime'"
        :id="fieldId"
        class="form-control"
        type="datetime-local"
        :value="toLocalDateTime(node.value)"
        :disabled="disabled"
        v-on:change="commit(fromLocalDateTime($event.target.value))"
      />
      <input
        v-else-if="node.input === 'time'"
        :id="fieldId"
        class="form-control"
        type="time"
        :value="node.value || ''"
        :step="timeStep"
        :disabled="disabled"
        v-on:input="typed(normalizeTime($event.target.value))"
        v-on:change="commit(normalizeTime($event.target.value))"
      />
      <input
        v-else
        :id="fieldId"
        class="form-control"
        :type="node.input === 'number' ? 'number' : node.input === 'search' ? 'search' : 'text'"
        :value="node.value ?? ''"
        :placeholder="node.placeholder"
        :min="node.min"
        :max="node.max"
        :step="node.step"
        :disabled="disabled"
        v-on:input="typed(fieldValue($event.target))"
        v-on:change="commit(fieldValue($event.target))"
      />
    </template>
    <p v-if="node.help" class="help-block">{{ node.help }}</p>
  </div>
</template>

<script>
import { useId } from 'vue'
import { DateTime } from 'luxon'
import { useMaxInterface } from '#max-runtime/context.ts'
import { normalizeTime } from '#max-core'
import DatePicker from '~/components/DatePicker.vue'
import MaxPosPicker from '~/components/MaxPosPicker.vue'

const CORE_INPUTS = ['chips', 'segmented', 'radio', 'checkbox', 'checkboxes', 'stepper', 'slider']

export default {
  components: { DatePicker, MaxPosPicker },
  props: ['node'],
  setup () {
    // Inputs POS has no screen for of its own are drawn by the Max renderer in the POS look
    return { iface: useMaxInterface(), fieldId: 'max-pos-field-' + useId(), CORE_INPUTS, normalizeTime }
  },
  computed: {
    disabled () {
      return this.node.disabled === true
    },
    /** The spec gives the step in minutes, the time input takes seconds */
    timeStep () {
      return typeof this.node.step === 'number' && this.node.step >= 1 ? Math.round(this.node.step) * 60 : 60
    },
    shortLocale () {
      return (this.$store && this.$store.getters.locale ? this.$store.getters.locale : 'fi_FI').split('_')[0]
    }
  },
  created () {
    this.timer = null
    this.committed = undefined
  },
  beforeUnmount () {
    if (this.timer) clearTimeout(this.timer)
  },
  methods: {
    timezone () {
      return (this.$store && this.$store.getters.timezone) || 'Europe/Helsinki'
    },
    /** The field's state, or one entry of a map state when the field has a key (one field per repeated item) */
    write (value) {
      if (this.node.stateKey !== undefined) this.iface.setIn(this.node.state, this.node.stateKey, value)
      else this.iface.setState(this.node.state, value)
    },
    typed (value) {
      this.write(value)
    },
    commit (value) {
      if (this.timer) clearTimeout(this.timer)
      this.write(value)
      if (this.committed === value) return
      this.committed = value
      const change = this.node.on && this.node.on.change
      if (change) this.iface.run(change, { ...(this.node.scope || {}), event: { value } })
    },
    fieldValue (el) {
      if (this.node.input !== 'number') return el.value
      return el.value === '' ? null : Number(el.value)
    },
    toIso (value) {
      if (!value) return ''
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
      const dt = value instanceof Date
        ? DateTime.fromJSDate(value, { zone: this.timezone() })
        : DateTime.fromISO(String(value), { zone: this.timezone() })
      return dt.isValid ? dt.toISODate() : ''
    },
    toLocalDateTime (value) {
      if (!value) return ''
      const dt = DateTime.fromISO(String(value), { zone: this.timezone() })
      return dt.isValid ? dt.toFormat("yyyy-LL-dd'T'HH:mm") : ''
    },
    fromLocalDateTime (value) {
      if (!value) return ''
      const dt = DateTime.fromISO(String(value), { zone: this.timezone() })
      return dt.isValid ? dt.toISO({ suppressMilliseconds: true }) : ''
    }
  }
}
</script>
